import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAppContext } from "@/app/providers/AppContext";
import WatchCreateWizard, {
  type WatchCreationPreview,
  type WatchCopyMode,
  type WatchWizardInitialState,
} from "@/domains/watches/components/WatchCreateWizard";
import type { WatchContractsPayload } from "@/domains/watches/lib/watchTypes";
import { decodeWatchRecipeCatalog } from "@/domains/watches/lib/watchRecipeCatalog";
import { decodeWatchOptionEvidenceCapability } from "@/domains/watches/lib/watchOptionEvidenceCapabilities";
import type { PositionExitEvaluationSpec } from "@/shared/lib/types";
import type { WatchDetail } from "@/domains/watches/components/WatchDetailPanel";
import { makeMultiLegDraftFromReadablePosition } from "@/domains/watches/lib/multiLegPosition";
import { decodeTickerSuggestion } from "@/domains/watches/lib/watchTickerSuggestion";
import {
  getWatchAuthoringSignalCatalog,
  getWatchWizardCapabilities,
} from "@/domains/watches/lib/watchAuthoringCapabilities";
import type {
  CanonicalPositionPayoffAvailability,
  PositionPayoffUnavailableReason,
} from "@/domains/watches/lib/watchPositionExitDraft";

// Copying must not silently discard rules that the fixed form slots cannot represent.
function positionCopyRuleError(settings: WatchDetail["settings"]): string | null {
  const unsupported = "This Watch has alert rules that this form cannot preserve. Its existing rules have not been changed. Return to Watches to review them, or start a new Watch.";
  const spec = settings?.evaluation_spec;
  const slots = new Set<string>();
  if (spec != null) {
    if (spec.schema_version !== 1 || spec.subject !== "POSITION_EXIT" || !("triggers" in spec) || !Array.isArray(spec.triggers)
      || (spec.logic != null && spec.logic !== "AND" && spec.logic !== "OR")) return unsupported;
    for (const trigger of spec.triggers) {
      if (!trigger || typeof trigger !== "object") return unsupported;
      const slot = trigger.type === "pnl_usd" ? `${trigger.type}:${trigger.operator}` : trigger.type;
      if (typeof slot !== "string" || !["pnl_usd:gte", "pnl_usd:lte", "max_profit_pct", "max_loss_pct", "dte", "short_strike_breach"].includes(slot)
        || slots.has(slot)) return unsupported;
      slots.add(slot);
    }
  } else {
    if (settings?.condition_logic != null && !["AND", "OR"].includes(settings.condition_logic)) return unsupported;
    for (const raw of settings?.conditions || []) {
      if (!raw || typeof raw !== "object") return unsupported;
      const condition = raw as { dimension?: unknown; operator?: unknown; threshold?: unknown };
      if (condition.dimension !== "position_unrealized_pnl_usd"
        || (condition.operator !== "gte" && condition.operator !== "lte")
        || condition.threshold == null || !Number.isFinite(Number(condition.threshold))
        || slots.has(condition.operator)) return unsupported;
      slots.add(condition.operator);
    }
  }
  return null;
}

type WatchPreviewResponse = {
  creation_preview?: WatchCreationPreview;
  confirmation_token?: string;
  payload_hash?: string;
};

type WatchCreateResponse = {
  watch_id?: string;
};

type TickerSearchResponse = {
  results?: unknown[];
};

type PositionPayoffPreviewResponse = {
  schema_version?: unknown;
  source?: unknown;
  max_profit_percent?: { available?: unknown; unavailable_reason?: unknown; maximum_usd?: unknown };
  max_loss_percent?: { available?: unknown; unavailable_reason?: unknown; maximum_usd?: unknown };
};

const PAYOFF_UNAVAILABLE_REASONS = new Set<PositionPayoffUnavailableReason>([
  "SINGLE_OPTION_UNSUPPORTED",
  "FINITE_PAYOFF_UNAVAILABLE",
  "ZERO_MAX_PROFIT",
  "ZERO_MAX_LOSS",
]);

function decodePayoffReason(value: unknown): PositionPayoffUnavailableReason | null {
  return typeof value === "string" && PAYOFF_UNAVAILABLE_REASONS.has(value as PositionPayoffUnavailableReason)
    ? value as PositionPayoffUnavailableReason
    : null;
}

function hasExactKeys(value: unknown, expected: readonly string[]): value is Record<string, unknown> {
  return typeof value === "object"
    && value !== null
    && !Array.isArray(value)
    && Object.keys(value).sort().join("|") === [...expected].sort().join("|");
}

function hasPayoffItemKeys(value: unknown) {
  return hasExactKeys(value, ["available", "unavailable_reason"])
    || hasExactKeys(value, ["available", "unavailable_reason", "maximum_usd"]);
}

function decodePositiveUsd(value: unknown): string | null {
  if (typeof value !== "string" || !/^\d+(?:\.\d{1,2})?$/.test(value)) return null;
  return Number.isFinite(Number(value)) && Number(value) > 0 ? value : null;
}

/**
 * The routed authoring surface deliberately shares the canonical wizard with
 * embedded callers. The page owns navigation and data loading; the wizard
 * owns only field state, preview confirmation, and payload construction.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Check the complete editable shape before a stale router payload reaches the
 * wizard. Economic validity is still checked by the normal form and server. */
function looksLikeWatchWizardInitialState(value: unknown): value is WatchWizardInitialState {
  if (!isRecord(value) || value.goal !== "position" || !isRecord(value.draft)) return false;
  const draft = value.draft;
  const legCounts: Record<string, number> = { single: 1, vertical: 2, iron_condor: 4, iron_butterfly: 4 };
  if (typeof draft.structure !== "string" || !Object.hasOwn(legCounts, draft.structure)
    || typeof draft.openedAt !== "number" || !Number.isFinite(draft.openedAt)
    || typeof draft.underlying !== "string" || typeof draft.expiry !== "string"
    || !Array.isArray(draft.legs) || draft.legs.length !== legCounts[draft.structure]) return false;
  if (!draft.legs.every((leg) => isRecord(leg)
    && (leg.side === "LONG" || leg.side === "SHORT")
    && (leg.optionType === "call" || leg.optionType === "put")
    && ["strike", "optionContract", "contracts", "entryPremiumPerShare"].every((key) => typeof leg[key] === "string"))) return false;
  if (draft.strategyEntryBasisType !== undefined
    && (typeof draft.strategyEntryBasisType !== "string"
      || !["", "NET_DEBIT", "NET_CREDIT"].includes(draft.strategyEntryBasisType))) return false;
  if (draft.strategyEntryBasisPerShare !== undefined && typeof draft.strategyEntryBasisPerShare !== "string") return false;

  const stringFields = ["profitTarget", "lossLimit", "maxProfitPercent", "maxLossPercent", "dteThreshold", "pnlThreshold", "sourceLabel"];
  if (stringFields.some((key) => value[key] !== undefined && typeof value[key] !== "string")) return false;
  for (const key of ["duration", "interval"]) {
    if (value[key] !== undefined && (typeof value[key] !== "number" || !Number.isFinite(value[key]))) return false;
  }
  for (const key of ["shortStrikeBreach", "marketHoursOnly"]) {
    if (value[key] !== undefined && typeof value[key] !== "boolean") return false;
  }
  const choices: Record<string, readonly unknown[]> = {
    positionExitLogic: ["AND", "OR"],
    dteOperator: [null, "lte", "eq"],
    shortStrikeSide: [null, "PUT", "CALL", "ANY"],
    pnlDirection: ["profit", "loss"],
    alertMode: ["one_and_done", "keep_watching"],
    sourceKind: ["DESK_IDEA"],
  };
  if (Object.entries(choices).some(([key, allowed]) => value[key] !== undefined && !allowed.includes(value[key]))) return false;
  if (value.sourceKind === "DESK_IDEA" && (typeof value.sourceLabel !== "string" || value.copy !== undefined)) return false;
  if (value.copy !== undefined && (!isRecord(value.copy)
    || (value.copy.mode !== "duplicate" && value.copy.mode !== "replace") || typeof value.copy.sourceLabel !== "string")) return false;
  if (value.provenance !== undefined && (!isRecord(value.provenance)
    || value.provenance.kind !== "TRADE_IDEA_LEDGER" || typeof value.provenance.ledgerId !== "string")) return false;
  const relativeRule = value.relativeExitRule;
  if (relativeRule !== undefined && (!isRecord(relativeRule)
    || !["ruleKind", "targetValue", "stopValue"].every((key) => typeof relativeRule[key] === "string")
    || typeof relativeRule.dteFloor !== "number" || !Number.isFinite(relativeRule.dteFloor))) return false;
  return true;
}

export default function WatchCreatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { api, loadWatches, prefs } = useAppContext();
  const [watchContracts, setWatchContracts] = useState<WatchContractsPayload | null>(null);
  const [contractsAttempt, setContractsAttempt] = useState(0);
  const [contractsState, setContractsState] = useState<"loading" | "ready" | "error">("loading");
  const [sourceDetail, setSourceDetail] = useState<WatchDetail | null>(null);
  const [sourceError, setSourceError] = useState("");
  const createInFlightRef = useRef<Promise<void> | null>(null);
  const previewConfirmationRef = useRef<{ confirmationToken: string; payloadHash: string } | null>(null);
  const draftRevisionRef = useRef(0);
  const previewRequestRef = useRef(0);
  // JS-1081 round 4 (adversarial-review FIX 4): the routed
  // `watchWizardInitialState` must be consumed EXACTLY ONCE. Without this,
  // a customer who opens a Desk-idea "+Watch" pre-fill, cancels back to the
  // list (a PUSH navigation -- see `onClose` below -- which leaves the
  // `/portal/watches/new` history entry, and its `location.state`, intact),
  // and then presses the browser's own Back button returns to that SAME
  // history entry with the ORIGINAL `watchWizardInitialState` still
  // attached -- silently re-seeding the wizard with pricing that may
  // already be stale (the whole point of FIX 1 in round 3 was that this
  // pre-fill's absolute thresholds must never be frozen/reused past their
  // moment of computation). Guarding with a `useRef` (defense in depth,
  // never removed) is not sufficient by itself, since a full remount (a
  // fresh page load via Back, not a React-internal re-render) resets every
  // ref back to its initial value -- so the actual fix has to be at the
  // HISTORY level: replace this entry's own `state` with `null` right after
  // the very first hydration, so a subsequent Back can never find the
  // original payload there again, regardless of whether the component
  // remounted.
  const consumedRoutedInitialStateRef = useRef(false);
  const sourceWatchId = searchParams.get("sourceWatchId")?.trim() || "";
  const modeValue = searchParams.get("mode");
  const copyMode: WatchCopyMode | null = modeValue === "duplicate" || modeValue === "replace" ? modeValue : null;

  useEffect(() => {
    let active = true;

    api<WatchContractsPayload>("GET", "/api/watches/contracts", undefined, { silent: true })
      .then((contracts) => {
        if (!active) return;
        setWatchContracts(contracts ?? null);
        setContractsState("ready");
      })
      .catch(() => {
        // Position monitoring remains available. Server-gated goals stay
        // unavailable until authenticated authoring metadata arrives.
        if (active) setContractsState("error");
      });

    return () => {
      active = false;
    };
  }, [api, contractsAttempt]);

  useEffect(() => {
    if (!sourceWatchId || !copyMode) return;
    let active = true;
    api<WatchDetail>("GET", `/api/watches/${encodeURIComponent(sourceWatchId)}`)
      .then((detail) => {
        if (active) setSourceDetail(detail ?? null);
      })
      .catch((reason) => {
        if (active) setSourceError(reason instanceof Error ? reason.message : "Unable to load the selected Watch.");
      });
    return () => { active = false; };
  }, [api, copyMode, sourceWatchId]);

  const sourceRuleError = sourceDetail ? positionCopyRuleError(sourceDetail.settings) : null;

  const initialState = useMemo<WatchWizardInitialState | null>(() => {
    if (!sourceWatchId || !copyMode || !sourceDetail) {
      // JS-1081: a genuinely SEPARATE source -- a Desk idea's "+Watch"
      // navigation, which passes its own pre-built WatchWizardInitialState
      // via router state instead of the sourceWatchId/copyMode query-param
      // flow above. This branch only ever runs when that existing flow's
      // condition is false, so the existing duplicate/replace path above
      // remains completely unchanged in code and behavior.
      const routedInitialState = (location.state as { watchWizardInitialState?: unknown } | null)?.watchWizardInitialState;
      return looksLikeWatchWizardInitialState(routedInitialState) ? routedInitialState : null;
    }
    const lifecycle = sourceDetail.watch.lifecycle?.state?.toUpperCase();
    if (lifecycle !== "ACTIVE" && lifecycle !== "PAUSED") return null;
    if (sourceRuleError) return null;
    const draft = makeMultiLegDraftFromReadablePosition(sourceDetail.watch.position);
    if (!draft) return null;
    const positionConditions = (sourceDetail.settings?.conditions || []).filter((condition) => {
      const candidate = condition as { dimension?: unknown; operator?: unknown; threshold?: unknown };
      return candidate.dimension === "position_unrealized_pnl_usd" && Number.isFinite(Number(candidate.threshold));
    }) as Array<{ operator?: string; threshold?: number }>;
    const profitCondition = positionConditions.find((condition) => condition.operator === "gte");
    const lossCondition = positionConditions.find((condition) => condition.operator === "lte");
    const positionExitSpec = sourceDetail.settings?.evaluation_spec?.subject === "POSITION_EXIT"
      ? sourceDetail.settings.evaluation_spec as PositionExitEvaluationSpec
      : null;
    const positionExitTriggers = positionExitSpec?.triggers || [];
    const profitTrigger = positionExitTriggers.find((trigger) => trigger.type === "pnl_usd" && trigger.operator === "gte");
    const lossTrigger = positionExitTriggers.find((trigger) => trigger.type === "pnl_usd" && trigger.operator === "lte");
    const maxProfitTrigger = positionExitTriggers.find((trigger) => trigger.type === "max_profit_pct");
    const maxLossTrigger = positionExitTriggers.find((trigger) => trigger.type === "max_loss_pct");
    const dteTrigger = positionExitTriggers.find((trigger) => trigger.type === "dte");
    const shortStrikeTrigger = positionExitTriggers.find((trigger) => trigger.type === "short_strike_breach");
    const policy = sourceDetail.settings?.policy || {};
    const maxAlerts = Number(policy.max_alerts_per_watch);
    return {
      goal: "position",
      draft,
      positionExitLogic: positionExitSpec
        ? positionExitSpec.logic === "AND" ? "AND" : "OR"
        : sourceDetail.settings?.condition_logic === "OR" ? "OR" : "AND",
      profitTarget: profitTrigger?.threshold != null
        ? String(Math.abs(Number(profitTrigger.threshold)))
        : profitCondition ? String(Math.abs(Number(profitCondition.threshold))) : "",
      lossLimit: lossTrigger?.threshold != null
        ? String(Math.abs(Number(lossTrigger.threshold)))
        : lossCondition ? String(Math.abs(Number(lossCondition.threshold))) : "",
      maxProfitPercent: maxProfitTrigger?.threshold != null ? String(maxProfitTrigger.threshold) : "",
      maxLossPercent: maxLossTrigger?.threshold != null ? String(maxLossTrigger.threshold) : "",
      dteThreshold: dteTrigger?.threshold != null ? String(dteTrigger.threshold) : "",
      dteOperator: dteTrigger?.operator === "eq" ? "eq" : dteTrigger ? "lte" : null,
      shortStrikeBreach: Boolean(shortStrikeTrigger),
      shortStrikeSide: shortStrikeTrigger?.side === "PUT" || shortStrikeTrigger?.side === "CALL" || shortStrikeTrigger?.side === "ANY"
        ? shortStrikeTrigger.side
        : shortStrikeTrigger ? "ANY" : null,
      duration: Number(policy.max_duration_days) || 5,
      interval: Number(policy.evaluation_interval_minutes) || 15,
      alertMode: maxAlerts > 1 ? "keep_watching" : "one_and_done",
      marketHoursOnly: policy.market_hours_only !== false,
      copy: { mode: copyMode, sourceLabel: `${draft.underlying} ${draft.expiry}` },
    };
  }, [copyMode, location.state, sourceDetail, sourceRuleError, sourceWatchId]);
  // JS-1081 round 4 (adversarial-review FIX 4): consume the routed
  // `watchWizardInitialState` EXACTLY ONCE by replacing this history
  // entry's own `state` with `null` right after the first render that
  // observes it -- see `consumedRoutedInitialStateRef`'s declaration above
  // for the full "cancel then browser Back" scenario this closes.
  // `initialState` itself is READ from `location.state` above (inside the
  // `useMemo`) on every render, but `useWatchAuthoringState`'s underlying
  // `useReducer` only consults its lazy-init argument on the wizard's OWN
  // first mount -- so clearing `location.state` after that first read never
  // resets a customer's in-progress edits; it only prevents a LATER
  // (re-)mount of this page (a browser Back navigation is a fresh mount,
  // not a live re-render) from finding the stale payload still attached to
  // this history entry. Effect-guarded (not done inline during render) so
  // it fires after commit, exactly once for a genuinely routed pre-fill.
  useEffect(() => {
    if (consumedRoutedInitialStateRef.current) return;
    const routedState = location.state as { watchWizardInitialState?: unknown } | null;
    if (!isRecord(routedState) || !("watchWizardInitialState" in routedState)) return;
    consumedRoutedInitialStateRef.current = true;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const wizardCapabilities = useMemo(
    () => getWatchWizardCapabilities(watchContracts),
    [watchContracts],
  );
  const signalCatalog = useMemo(
    () => getWatchAuthoringSignalCatalog(watchContracts),
    [watchContracts],
  );
  const recipeCatalog = useMemo(
    () => decodeWatchRecipeCatalog(watchContracts?.recipe_catalog),
    [watchContracts],
  );
  const optionEvidenceCapability = useMemo(
    () => decodeWatchOptionEvidenceCapability(watchContracts?.option_evidence_clauses),
    [watchContracts],
  );

  const previewWatch = async (body: Record<string, unknown>): Promise<WatchCreationPreview> => {
    previewConfirmationRef.current = null;
    const draftRevision = draftRevisionRef.current;
    const previewRequest = previewRequestRef.current + 1;
    previewRequestRef.current = previewRequest;
    const response = await api<WatchPreviewResponse>(
      "POST",
      copyMode && sourceWatchId
        ? `/api/watches/${encodeURIComponent(sourceWatchId)}/${copyMode}/preview`
        : "/api/watches/preview",
      body,
    );
    if (draftRevision !== draftRevisionRef.current || previewRequest !== previewRequestRef.current) {
      throw new Error("The Watch changed while it was being checked. Review the latest settings.");
    }
    if (!response?.creation_preview || typeof response.creation_preview !== "object") {
      throw new Error("The server did not return a Watch validation preview. Try again.");
    }
    if (typeof response.confirmation_token !== "string" || typeof response.payload_hash !== "string") {
      throw new Error("The server could not secure this Watch preview. Try again.");
    }
    previewConfirmationRef.current = {
      confirmationToken: response.confirmation_token,
      payloadHash: response.payload_hash,
    };
    return response.creation_preview;
  };

  const createWatch = (body: Record<string, unknown>): Promise<void> => {
    void body;
    if (createInFlightRef.current) return createInFlightRef.current;
    const previewConfirmation = previewConfirmationRef.current;
    if (!previewConfirmation) {
      return Promise.reject(new Error("This Watch preview is no longer available. Go back and review it again."));
    }

    const operation = (async () => {
      // Keep the same confirmation on an uncertain response. The server binds
      // it to one durable operation and replays its result without duplication.
      const created = await api<WatchCreateResponse>(
        "POST",
        copyMode && sourceWatchId
          ? `/api/watches/${encodeURIComponent(sourceWatchId)}/${copyMode}`
          : "/api/watches/confirm",
        {
          confirmation_token: previewConfirmation.confirmationToken,
          payload_hash: previewConfirmation.payloadHash,
        },
      );
      if (previewConfirmationRef.current === previewConfirmation) {
        previewConfirmationRef.current = null;
      }
      const watchId = typeof created?.watch_id === "string" ? created.watch_id : "";

      toast.success(copyMode === "replace" ? "Replacement Watch created." : copyMode === "duplicate" ? "Watch duplicated." : "Watch created.", {
        description: watchId ? "Its details are open in your Watches list." : undefined,
      });

      try {
        await loadWatches();
      } catch {
        // The mutation succeeded. A read failure must not invite another creation.
        toast.warning("Your Watch was saved, but the list could not refresh. Reload Watches to see it.");
      }
      if (!watchId) {
        navigate("/portal/watches", { replace: true });
        return;
      }

      navigate(`/portal/watches?watchId=${encodeURIComponent(watchId)}`, { replace: true });
    })();
    const pending = operation.finally(() => {
      if (createInFlightRef.current === pending) createInFlightRef.current = null;
    });
    createInFlightRef.current = pending;
    return pending;
  };

  const searchTickers = useCallback(async (query: string) => {
    const response = await api<TickerSearchResponse>(
      "GET",
      `/api/tickers/search?query=${encodeURIComponent(query.trim().toUpperCase())}`,
      undefined,
      { silent: true },
    );
    return (response?.results || []).flatMap((item) => {
      const suggestion = decodeTickerSuggestion(item);
      return suggestion ? [suggestion] : [];
    });
  }, [api]);

  const previewPositionPayoff = useCallback(async (
    payload: Record<string, unknown>,
    signal: AbortSignal,
  ): Promise<CanonicalPositionPayoffAvailability> => {
    const response = await api<PositionPayoffPreviewResponse>(
      "POST",
      "/api/watches/position-payoff-preview",
      payload,
      { silent: true, signal },
    );
    const maxProfit = response?.max_profit_percent;
    const maxLoss = response?.max_loss_percent;
    const maxProfitAvailable = maxProfit?.available;
    const maxLossAvailable = maxLoss?.available;
    const maxProfitUsd = decodePositiveUsd(maxProfit?.maximum_usd);
    const maxLossUsd = decodePositiveUsd(maxLoss?.maximum_usd);
    const maxProfitReason = decodePayoffReason(maxProfit?.unavailable_reason);
    const maxLossReason = decodePayoffReason(maxLoss?.unavailable_reason);
    if (
      !hasExactKeys(response, ["schema_version", "source", "max_profit_percent", "max_loss_percent"])
      || response.schema_version !== 1
      || response.source !== "SERVER_PAYOFF_PREVIEW"
      || !hasPayoffItemKeys(maxProfit)
      || !hasPayoffItemKeys(maxLoss)
      || typeof maxProfitAvailable !== "boolean"
      || typeof maxLossAvailable !== "boolean"
      || (maxProfitAvailable ? maxProfit?.unavailable_reason !== null : maxProfitReason === null)
      || (maxLossAvailable ? maxLoss?.unavailable_reason !== null : maxLossReason === null)
      || (maxProfitAvailable && maxProfit?.maximum_usd !== undefined && maxProfitUsd === null)
      || (maxLossAvailable && maxLoss?.maximum_usd !== undefined && maxLossUsd === null)
      || (!maxProfitAvailable && maxProfit?.maximum_usd != null)
      || (!maxLossAvailable && maxLoss?.maximum_usd != null)
    ) {
      throw new Error("The server returned an invalid payoff preview.");
    }
    return {
      source: "SERVER_PAYOFF_PREVIEW",
      maxProfitPercent: maxProfitAvailable,
      maxLossPercent: maxLossAvailable,
      maxProfitUsd,
      maxLossUsd,
      maxProfitUnavailableReason: maxProfitAvailable
        ? null
        : maxProfitReason,
      maxLossUnavailableReason: maxLossAvailable
        ? null
        : maxLossReason,
    };
  }, [api]);

  if (sourceWatchId && copyMode && !sourceDetail && !sourceError) {
    return <main aria-busy="true" aria-live="polite">Loading the selected Watch…</main>;
  }

  if (sourceWatchId && copyMode && (!initialState || sourceError)) {
    return <main role="alert"><h1>That Watch cannot be {copyMode === "replace" ? "replaced" : "duplicated"}</h1><p>{sourceError || sourceRuleError || "Only an active or paused supported option-position Watch can start this flow."}</p><button type="button" onClick={() => navigate("/portal/watches")}>Back to Watches</button></main>;
  }

  return (
    <WatchCreateWizard
      presentation="page"
      onClose={() => navigate("/portal/watches")}
      onPreview={previewWatch}
      onCreate={createWatch}
      marketDataApi={api}
      onPositionPayoffPreview={previewPositionPayoff}
      searchTickers={searchTickers}
      capabilities={wizardCapabilities}
      signalCatalog={signalCatalog}
      recipeCatalog={recipeCatalog}
      optionEvidenceCapability={optionEvidenceCapability}
      capabilityState={contractsState}
      onDraftChange={() => {
        draftRevisionRef.current += 1;
        previewRequestRef.current += 1;
        previewConfirmationRef.current = null;
      }}
      onRetryCapabilities={() => {
        setContractsState("loading");
        setContractsAttempt((attempt) => attempt + 1);
      }}
      notificationPrefs={{
        email: Boolean(prefs?.channels?.email?.enabled && prefs.channels.email.address)
          || prefs?.notification_channels?.some((channel) => channel.type === "email" && channel.enabled),
        sms: Boolean(prefs?.channels?.sms?.enabled && prefs.channels.sms.phone_number)
          || prefs?.notification_channels?.some((channel) => channel.type === "sms" && channel.enabled),
        telegram: Boolean(prefs?.channels?.telegram?.enabled && prefs.channels.telegram.chat_id),
        slack: Boolean(prefs?.channels?.slack?.enabled && prefs.channels.slack.webhook_url),
        whatsapp: Boolean(prefs?.channels?.whatsapp?.enabled && prefs.channels.whatsapp.phone_number),
        push: prefs?.notification_channels?.some((channel) => channel.type === "push" && channel.enabled),
        inApp: prefs?.notification_channels?.some((channel) => channel.type === "in_app" && channel.enabled),
      }}
      initialState={initialState ?? undefined}
    />
  );
}
